import type { WatchlistItem } from "@/services/api/types";

export type WatchlistExportColumnKey =
  | "symbol"
  | "display_name"
  | "close"
  | "pct_chg"
  | "change"
  | "trade_date"
  | "market"
  | "alias"
  | "note"
  | "pinned";

interface WatchlistExportColumnDef {
  key: WatchlistExportColumnKey;
  label: string;
  value: (item: WatchlistItem) => string;
}

const COLUMN_DEFS: WatchlistExportColumnDef[] = [
  { key: "symbol", label: "股票代码", value: (item) => toCell(item.symbol) },
  {
    key: "display_name",
    label: "股票名称",
    value: (item) => toCell(item.display_name || item.stock_name || item.name_cache || item.symbol),
  },
  { key: "close", label: "最新价", value: (item) => toCell(item.close) },
  { key: "pct_chg", label: "涨跌幅(%)", value: (item) => toCell(item.pct_chg) },
  { key: "change", label: "涨跌额", value: (item) => toCell(item.change) },
  { key: "trade_date", label: "交易日", value: (item) => toCell(item.trade_date) },
  { key: "market", label: "市场", value: (item) => toCell(item.market) },
  { key: "alias", label: "别名", value: (item) => toCell(item.alias) },
  { key: "note", label: "备注", value: (item) => toCell(item.note) },
  { key: "pinned", label: "置顶", value: (item) => (item.pinned ? "1" : "0") },
];

export const WATCHLIST_EXPORT_COLUMNS: Array<{ key: WatchlistExportColumnKey; label: string }> = COLUMN_DEFS.map((item) => ({
  key: item.key,
  label: item.label,
}));

export function normalizeExportColumnKeys(columnKeys: WatchlistExportColumnKey[]): WatchlistExportColumnKey[] {
  const deduped = new Set<WatchlistExportColumnKey>(columnKeys);
  deduped.delete("symbol");
  return ["symbol", ...Array.from(deduped)];
}

export function buildWatchlistCsv(rows: WatchlistItem[], columnKeys: WatchlistExportColumnKey[]): string {
  const normalizedKeys = normalizeExportColumnKeys(columnKeys);
  const defs = normalizedKeys
    .map((key) => COLUMN_DEFS.find((item) => item.key === key))
    .filter((item): item is WatchlistExportColumnDef => Boolean(item));
  const header = defs.map((def) => escapeCsv(def.label)).join(",");
  const body = rows
    .map((row) =>
      defs
        .map((def) => escapeCsv(def.value(row)))
        .join(","),
    )
    .join("\n");
  return `\uFEFF${header}${body ? `\n${body}` : ""}`;
}

export function makeWatchlistExportFilename(watchlistName: string, now = new Date()): string {
  const normalizedName = (watchlistName || "watchlist").replace(/[\\/:*?"<>|]+/g, "_").trim() || "watchlist";
  const stamp = now.toISOString().slice(0, 10);
  return `${normalizedName}-${stamp}.csv`;
}

function toCell(value: unknown): string {
  if (value == null) {
    return "";
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "";
  }
  return String(value);
}

function escapeCsv(value: string): string {
  if (!value) {
    return "";
  }
  if (!/[",\n\r]/.test(value)) {
    return value;
  }
  return `"${value.replaceAll('"', '""')}"`;
}
